/**
 * Card-style CTA banner with glow ring visual treatment.
 *
 * Dark gradient background with centered radial glow, concentric rotating rings,
 * subtle grid texture, and a motion fade-up entrance.
 * Builder.io-registrable.
 *
 * Aliases: cta banner, call to action, marketing banner, glow ring cta
 */

import { Link } from "@tanstack/react-router";
import { motion } from "motion/react";
import type { ReactNode } from "react";

import { Button, type ButtonProps } from "~/components/ui/button";
import { safeBuilderText } from "~/lib/builder-utils";
import { cn } from "~/lib/cn";

type CtaBannerProps = {
  readonly headline?: unknown;
  readonly description?: unknown;
  readonly buttonText?: unknown;
  readonly buttonHref?: unknown;
  readonly buttonVariant?: "fancy" | "default" | "primary";
  readonly buttonTrack?: ButtonProps["track"];
  readonly className?: string;
  readonly children?: ReactNode;
};

const rings = [
  {
    size: "w-[280px] h-[280px]",
    opacity: "opacity-[0.10]",
    anim: "animate-spin-ring",
  },
  {
    size: "w-[420px] h-[420px]",
    opacity: "opacity-[0.07]",
    anim: "animate-spin-ring-slow",
  },
  {
    size: "w-[580px] h-[580px]",
    opacity: "opacity-[0.04]",
    anim: "animate-spin-ring-slower",
  },
] as const;

export const CtaBanner = ({
  headline,
  description,
  buttonText,
  buttonHref,
  buttonVariant = "fancy",
  buttonTrack,
  className,
  children,
}: CtaBannerProps) => {
  const safeHeadline = safeBuilderText(headline);
  const safeDescription = safeBuilderText(description);
  const safeButtonText = safeBuilderText(buttonText);
  const safeButtonHref = safeBuilderText(buttonHref);

  if (!safeHeadline && !safeDescription) return null;

  const button = safeButtonText ? (
    <div className="mt-6 flex justify-center sm:mt-8">
      {safeButtonHref ? (
        <Link to={safeButtonHref}>
          <Button
            size="lg"
            variant={buttonVariant}
            track={buttonTrack}
            className="px-8 shadow-[0_0_28px_rgba(168,85,247,0.35)]"
          >
            {safeButtonText}
          </Button>
        </Link>
      ) : (
        <Button
          size="lg"
          variant={buttonVariant}
          track={buttonTrack}
          className="px-8 shadow-[0_0_28px_rgba(168,85,247,0.35)]"
        >
          {safeButtonText}
        </Button>
      )}
    </div>
  ) : null;

  return (
    <motion.section
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 0.6, ease: "easeOut" }}
      className={cn(
        "relative overflow-hidden rounded-2xl bg-gradient-to-b from-[#0f0524] via-[#1a0a3e] to-[#0f0524]",
        className,
      )}
    >
      {/* Radial glow */}
      <div
        aria-hidden="true"
        className="absolute top-1/2 left-1/2 h-[500px] w-[500px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[radial-gradient(circle,rgba(168,85,247,0.15)_0%,transparent_70%)]"
      />

      {/* Concentric rings */}
      {rings.map((ring) => (
        <div
          key={ring.size}
          aria-hidden="true"
          className={cn(
            "absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full border border-purple-500",
            ring.size,
            ring.opacity,
            ring.anim,
          )}
        />
      ))}

      {/* Grid texture */}
      <div
        aria-hidden="true"
        className="absolute inset-0 opacity-[0.02]"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)",
          backgroundSize: "40px 40px",
        }}
      />

      {/* Content */}
      <div className="relative z-10 px-6 py-10 text-center text-white sm:px-8 sm:py-14">
        <div className="mx-auto max-w-lg">
          {safeHeadline && (
            <h2 className="font-heading text-2xl font-medium tracking-tight sm:text-3xl">
              {safeHeadline}
            </h2>
          )}
          {safeDescription && (
            <p className="mt-3 text-sm leading-relaxed text-purple-200/70 sm:mt-4 sm:text-base">
              {safeDescription}
            </p>
          )}
          {button}
          {children && (
            <div className="flex flex-col items-center">{children}</div>
          )}
        </div>
      </div>
    </motion.section>
  );
};
